import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useStore } from "zustand";
import { getValidAttributeFilters } from "@/components/ui/chat/chatFilterUtils";
import { VocPeriodFilterType } from "@/models/vocPeriodFilter";
import { VocService } from "@/services/voc/voc.service";
import { getVocSearchStore } from "@/stores/ui/useVocSearchStore";
import { downloadCsv } from "@/utils/csv";
import { showError } from "@/utils/notification";
import { useWorkspaces } from "./useWorkspaces";

export const useVocSearch = () => {
  const { sessionCurrentWorkspaceId: workspaceId } = useWorkspaces();
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("voiceek_ui");
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    keyword,
    categories,
    periodFilter,
    attributeFilters,
    tagIds,
    result,
    setKeyword,
    setCategories,
    setSearchResults,
    updatePeriodFilter,
    updateAttributeFilter,
    toggleTagId,
    clearFilters,
  } = useStore(getVocSearchStore(workspaceId));

  const handleSearch = useCallback(async () => {
    if (!keyword || !workspaceId) return;

    setIsSearching(true);
    try {
      const validFilters = getValidAttributeFilters(attributeFilters);
      const result = await VocService.search({
        workspaceId,
        keyword,
        categories,
        startDate: periodFilter.type === VocPeriodFilterType.CUSTOM ? periodFilter.start ?? undefined : undefined,
        endDate: periodFilter.type === VocPeriodFilterType.CUSTOM ? periodFilter.end ?? undefined : undefined,
        attributeFilters: Object.keys(validFilters).length > 0 ? validFilters : undefined,
        tagIds: tagIds.length > 0 ? tagIds : undefined,
      });
      setSearchResults({
        ...result,
        attributeFilters: Object.keys(validFilters).length > 0 ? validFilters : undefined,
        tagIds: tagIds.length > 0 ? tagIds : undefined,
      });
    } catch (e) {
      toast.error(tCommon("errors.unknownError"));
      console.error(e);
      setSearchResults({
        vocs: [],
        total: 0,
        keyword,
        categories,
        startDate: periodFilter.type === VocPeriodFilterType.CUSTOM ? periodFilter.start ?? null : null,
        endDate: periodFilter.type === VocPeriodFilterType.CUSTOM ? periodFilter.end ?? null : null,
      });
    } finally {
      setIsSearching(false);
    }
  }, [keyword, categories, periodFilter, attributeFilters, tagIds, workspaceId, tCommon, setSearchResults]);

  const handleDownload = useCallback(async () => {
    if (!workspaceId || !keyword.trim()) return;

    setIsDownloading(true);
    try {
      const blob = await VocService.downloadSearchResult({
        workspaceId: workspaceId,
        keyword,
        categories,
        startDate: periodFilter.type === VocPeriodFilterType.CUSTOM ? periodFilter.start ?? undefined : undefined,
        endDate: periodFilter.type === VocPeriodFilterType.CUSTOM ? periodFilter.end ?? undefined : undefined,
      });
      const sanitizedKeyword = keyword.trim().replaceAll(/[ \u3000\r\n]/g, "_");
      downloadCsv(blob, `${sanitizedKeyword}.csv`);
    } catch (error) {
      console.error(error);
      showError(t("voc.search.download.error"));
    } finally {
      setIsDownloading(false);
    }
  }, [workspaceId, keyword, categories, periodFilter, t]);

  return {
    keyword,
    setKeyword,
    filters: {
      periodFilter,
      categories,
      attributeFilters,
      tagIds,
    },
    updateFilters: {
      updatePeriodFilter,
      updateCategories: setCategories,
      updateAttributeFilter,
      toggleTagId,
    },
    clearFilters,
    result,
    isSearching,
    isDownloading,
    handleSearch,
    handleDownload,
  };
};
